import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BRAND from '../brand';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$97',
    period: '/mo',
    desc: '1 bot · 500 conversations/month',
    features: ['1 AI support bot', '500 conversations/mo', 'Knowledge base upload', 'Email escalation', 'Basic analytics'],
  },
  {
    id: 'growth',
    name: 'Growth',
    price: '$297',
    period: '/mo',
    desc: '3 bots · 2,000 conversations/month',
    popular: true,
    features: ['3 AI support bots', '2,000 conversations/mo', 'Risk scoring + churn prediction', 'Revenue attribution', 'Playbooks & workflows', 'A/B testing', 'Priority support'],
  },
  {
    id: 'agency',
    name: 'Agency',
    price: '$597',
    period: '/mo',
    desc: 'Unlimited bots · Unlimited conversations',
    features: ['Unlimited bots', 'Unlimited conversations', 'White-label (no branding)', 'All integrations', 'Voice & call analysis', 'Executive summaries', 'Dedicated account manager'],
  },
];

export default function Signup() {
  const { initiateSignup } = useAuth();
  const navigate = useNavigate();

  const [step,     setStep]     = useState(1);       // 1 = account details, 2 = plan picker
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [plan,     setPlan]     = useState('growth'); // Default to Growth (most popular)
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const inp = {
    width: '100%', padding: '0.75rem 1rem',
    background: '#18181b', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, color: '#fafafa', fontSize: '0.9rem',
    fontFamily: 'Geist, sans-serif', outline: 'none',
  };

  // ── Step 1: validate account details and move to plan picker ──
  const handleAccountNext = (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim())  return setError('Please enter your name.');
    if (!email.trim()) return setError('Please enter your email.');
    if (password.length < 6) return setError('Password must be at least 6 characters.');
    if (password !== confirm)  return setError('Passwords do not match.');
    setStep(2);
  };

  // ── Step 2: call Stripe and redirect ─────────────────────────
const handleStartPilot = async () => {
  setLoading(true);
  setError('');

  const result = await initiateSignup(name.trim(), email.trim(), password, plan);

  setLoading(false);

  if (result?.error) {
    setError(result.error);
    return;
  }

  // DO NOT navigate to dashboard here
};
  const selectedPlan = PLANS.find(p => p.id === plan);

  return (
    <div style={{ minHeight: '100vh', background: '#09090b', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', fontFamily: 'Geist, sans-serif' }}>
      <div style={{ width: '100%', maxWidth: step === 2 ? 700 : 420 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: '1.75rem', color: '#fafafa', marginBottom: '0.5rem' }}>
            {BRAND.name}<span style={{ color: BRAND.accent }}>.</span>
          </div>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 600, color: '#fafafa', marginBottom: '0.4rem' }}>
            {step === 1 ? 'Create your account' : 'Choose your plan'}
          </h1>
          <p style={{ color: '#71717a', fontSize: '0.85rem' }}>
            {step === 1 ? '14-day free trial on every plan. No charge today.' : 'You\'ll be redirected to Stripe to complete payment.'}
          </p>
        </div>

        {/* Step progress dots */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '1.75rem' }}>
          {['Account', 'Plan'].map((label, i) => {
            const s = i + 1;
            const done = step > s;
            const active = step === s;
            return (
              <React.Fragment key={s}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.72rem', fontWeight: 700,
                    background: done ? BRAND.accent : active ? BRAND.accent : '#18181b',
                    color: done || active ? BRAND.accentText : '#71717a',
                    border: done || active ? 'none' : '1px solid rgba(255,255,255,0.1)',
                    transition: 'all 0.2s',
                  }}>
                    {done ? '✓' : s}
                  </div>
                  <span style={{ fontSize: '0.75rem', color: active ? '#fafafa' : '#71717a', fontWeight: active ? 500 : 400 }}>{label}</span>
                </div>
                {i < 1 && (
                  <div style={{ width: 40, height: 1, background: step > 1 ? BRAND.accent : 'rgba(255,255,255,0.1)', transition: 'background 0.3s' }}/>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Error banner */}
        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '1.25rem', fontSize: '0.85rem', color: '#fca5a5', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
            <span style={{ flexShrink: 0 }}>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* ── STEP 1: Account details ── */}
        {step === 1 && (
          <div style={{ background: '#111113', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '2rem' }}>
            <form onSubmit={handleAccountNext} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#a1a1aa', display: 'block', marginBottom: '0.4rem' }}>Full name</label>
                <input
                  type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="Jane Smith" required style={inp}
                  onFocus={e => e.target.style.borderColor = `rgba(245,158,11,0.6)`}
                  onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#a1a1aa', display: 'block', marginBottom: '0.4rem' }}>Email address</label>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" required style={inp}
                  onFocus={e => e.target.style.borderColor = `rgba(245,158,11,0.6)`}
                  onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#a1a1aa', display: 'block', marginBottom: '0.4rem' }}>Password</label>
                <input
                  type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="At least 6 characters" required style={inp}
                  onFocus={e => e.target.style.borderColor = `rgba(245,158,11,0.6)`}
                  onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: 500, color: '#a1a1aa', display: 'block', marginBottom: '0.4rem' }}>Confirm password</label>
                <input
                  type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                  placeholder="Repeat your password" required style={inp}
                  onFocus={e => e.target.style.borderColor = `rgba(245,158,11,0.6)`}
                  onBlur={e  => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                />
              </div>
              <button type="submit" style={{ width: '100%', padding: '0.85rem', background: BRAND.accent, color: BRAND.accentText, border: 'none', borderRadius: 8, fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'Geist, sans-serif', marginTop: '0.25rem' }}>
                Continue →
              </button>
            </form>
          </div>
        )}

        {/* ── STEP 2: Plan picker ── */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* Plan cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
              {PLANS.map(p => (
                <div
                  key={p.id}
                  onClick={() => setPlan(p.id)}
                  style={{
                    background: plan === p.id ? '#1a1400' : '#111113',
                    border: `2px solid ${plan === p.id ? BRAND.accent : 'rgba(255,255,255,0.07)'}`,
                    borderRadius: 12, padding: '1.25rem', cursor: 'pointer',
                    position: 'relative', transition: 'all 0.15s',
                  }}
                >
                  {p.popular && (
                    <div style={{ position: 'absolute', top: -1, left: '50%', transform: 'translateX(-50%)', background: BRAND.accent, color: BRAND.accentText, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', padding: '0.15rem 0.65rem', borderRadius: '0 0 6px 6px', whiteSpace: 'nowrap' }}>
                      Most popular
                    </div>
                  )}

                  {/* Radio + name */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', marginTop: p.popular ? '0.25rem' : 0 }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${plan === p.id ? BRAND.accent : 'rgba(255,255,255,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {plan === p.id && <div style={{ width: 7, height: 7, borderRadius: '50%', background: BRAND.accent }}/>}
                    </div>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#fafafa' }}>{p.name}</span>
                  </div>

                  {/* Price */}
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={{ fontFamily: 'Instrument Serif, serif', fontSize: '1.75rem', color: plan === p.id ? BRAND.accent : '#fafafa', lineHeight: 1 }}>{p.price}</span>
                    <span style={{ fontSize: '0.75rem', color: '#71717a' }}>{p.period}</span>
                  </div>

                  {/* Desc */}
                  <div style={{ fontSize: '0.72rem', color: '#71717a', marginBottom: '0.85rem' }}>{p.desc}</div>

                  {/* Features */}
                  <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    {p.features.map(f => (
                      <li key={f} style={{ fontSize: '0.72rem', color: '#a1a1aa', display: 'flex', gap: '0.35rem', alignItems: 'flex-start' }}>
                        <span style={{ color: BRAND.accent, flexShrink: 0, marginTop: '1px' }}>✓</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Trial notice */}
            <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 10, padding: '0.85rem 1rem', display: 'flex', gap: '0.65rem', alignItems: 'center' }}>
              <span style={{ fontSize: '1rem' }}>🎉</span>
              <div>
                <div style={{ fontSize: '0.83rem', fontWeight: 600, color: '#10b981' }}>14-day free trial — no charge today</div>
                <div style={{ fontSize: '0.75rem', color: '#71717a', marginTop: 2 }}>
                  Your card won't be charged until your trial ends. Cancel anytime before then.
                </div>
              </div>
            </div>

            {/* What happens next */}
            <div style={{ background: '#111113', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '0.85rem 1rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#a1a1aa', marginBottom: '0.5rem', letterSpacing: '0.5px', textTransform: 'uppercase' }}>What happens next</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {[
                  '1. Click the button below → you\'re taken to Stripe (Stripe is the payment processor — your card details never touch our server)',
                  '2. Enter your card details on Stripe\'s secure page',
                  '3. Stripe redirects you back here and activates your account with the ' + (selectedPlan?.name || '') + ' plan',
                  '4. No charge for 14 days',
                ].map((s, i) => (
                  <div key={i} style={{ fontSize: '0.78rem', color: '#71717a' }}>{s}</div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleStartPilot}
              disabled={loading}
              style={{ width: '100%', padding: '1rem', background: loading ? '#71717a' : BRAND.accent, color: BRAND.accentText, border: 'none', borderRadius: 10, fontSize: '1rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Geist, sans-serif', transition: 'background 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
            >
              {loading ? (
                <>
                  <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⟳</span>
                  Redirecting to Stripe...
                </>
              ) : (
                `Start 14-day trial on ${selectedPlan?.name} — ${selectedPlan?.price}/mo after →`
              )}
            </button>

            <button onClick={() => { setStep(1); setError(''); }} style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'Geist, sans-serif', padding: '0.25rem' }}>
              ← Back to account details
            </button>
          </div>
        )}

        <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.85rem', color: '#71717a' }}>
          Already have an account?{' '}
          <span style={{ color: BRAND.accent, cursor: 'pointer', fontWeight: 500 }} onClick={() => navigate('/login')}>
            Sign in
          </span>
        </p>

        <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
      </div>
    </div>
  );
}